import React from 'react';
import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import framework from 'utils/framework';
const { wrapFunctionalComponent } = framework;
import LanguageLocaleUtils from 'utils/LanguageLocale';
import {
    Box, Text, Button, Link
} from 'components/ui';
import UserActions from 'actions/UserActions';
import { trackForgotPasswordEmailResent } from 'pages/Auth/ResetPassword/analytics';
import Storage from 'utils/localStorage/Storage';
import LOCAL_STORAGE from 'constants/LocalStorage';
import UrlUtils from 'utils/Url';
import {
    colors, space, fontSizes, lineHeights, fontWeights
} from 'style/config';
import ResetPasswordEN from 'pages/Auth/ResetPassword/locales/ResetPassword_en_US';
import ResetPasswordFR from 'pages/Auth/ResetPassword/locales/ResetPassword_fr_CA';
import { SCREENS, ERROR_CODES } from 'pages/Auth/ResetPassword/constants';

const getText = LanguageLocaleUtils.getLocalisedDataFromResource(ResetPasswordEN, ResetPasswordFR);

/**
 * EMAIL_SENT — SIM-18 / ACS-4355 / SUB-TASK-4.
 *
 * Inline replacement for `ResetPasswordConfirmationModal` (Scenario 3/4).
 * NOT wrapped in `<Modal>` — renders in the page shell. Copy switches on
 * `isPasskeyFlow` (`confirmationMessagePasskey` vs `confirmationMessage2`).
 * Resend calls `UserActions.forgotPassword(email, onSuccess, onFailure,
 * 'resetPasswordConfirmationModal', isPasskeyFlow ? { pk: true } : {})` —
 * signature matches `ResetPasswordConfirmationModal.sendEmail`.
 *
 * OK button:
 *   - If `EXTERNAL_AUTH_PARAMS` is in local storage → invoke the SIM-1 OAuth
 *     back path (via UrlUtils.redirectTo('/login?...')).
 *   - Otherwise → transition back to the initial screen for this flow
 *     (PASSKEY_FIRST_LANDING when passkey, TRADITIONAL_RESET when not).
 */
const EmailSentConfirmation = ({ onTransition, sharedState }) => {
    const dispatch = useDispatch();
    const isPasskeyFlow = Boolean(sharedState?.isPasskeyFlow);
    const emailFromState = sharedState?.email || '';
    // BR-021: prefer the Redux user slice for display (matches SIM-1
    // CheckYourEmailModal) so a screen refresh keeps working.
    const loginFromRedux = useSelector(state => state?.user?.login) || '';
    const email = emailFromState || loginFromRedux;

    const [resendError, setResendError] = useState('');
    const [resendSuccess, setResendSuccess] = useState(false);

    const buildOauthQueryString = externalAuthParams => {
        /* eslint-disable camelcase */
        const oauthParams = {
            response_type: externalAuthParams.responseType,
            client_id: externalAuthParams.clientId,
            redirect_uri: externalAuthParams.redirectUri,
            scope: externalAuthParams.scope,
            state: externalAuthParams.state,
            code_challenge: externalAuthParams.codeChallenge,
            code_challenge_method: externalAuthParams.codeChallengeMethod,
            nonce: externalAuthParams.nonce,
            prompt: externalAuthParams.prompt,
            display: externalAuthParams.display,
            max_age: externalAuthParams.maxAge,
            ui_locales: externalAuthParams.uiLocales,
            id_token_hint: externalAuthParams.idTokenHint,
            login_hint: externalAuthParams.loginHint,
            acr_values: externalAuthParams.acrValues
        };
        /* eslint-enable camelcase */
        const filteredParams = Object.fromEntries(Object.entries(oauthParams).filter(([, value]) => value));

        return new URLSearchParams(filteredParams).toString();
    };

    const handleOk = () => {
        const externalAuthParams = Storage.local.getItem(LOCAL_STORAGE.EXTERNAL_AUTH_PARAMS);

        if (externalAuthParams) {
            // OAuth back path — SIM-1 semantics. `externalAuthParams` may be a
            // pre-encoded string or an object. When it is an object, re-build
            // the query; when a string, forward verbatim so the caller receives
            // the same URI it originally passed in.
            if (typeof externalAuthParams === 'string' && externalAuthParams.length > 0) {
                UrlUtils.redirectTo(`/login?${externalAuthParams}`);

                return;
            }

            if (typeof externalAuthParams === 'object' && externalAuthParams !== null) {
                const queryString = buildOauthQueryString(externalAuthParams);
                UrlUtils.redirectTo(queryString ? `/login?${queryString}` : '/login');

                return;
            }
        }

        onTransition(isPasskeyFlow ? SCREENS.PASSKEY_FIRST_LANDING : SCREENS.TRADITIONAL_RESET);
    };

    const handleResend = () => {
        setResendError('');
        setResendSuccess(false);
        const onSuccess = () => {
            trackForgotPasswordEmailResent(email);
            setResendSuccess(true);
        };
        const onFailure = response => {
            const errors = response?.errors || [];
            const errorCode = errors[0]?.errorCode;

            if (errorCode === ERROR_CODES.ERR_PK_MAX_ATTEMPTS) {
                setResendError(getText('errorPasskeyMaxAttempts'));
            } else {
                setResendError(errors[0]?.errorMessage || getText('errorMessage'));
            }
        };
        dispatch(UserActions.forgotPassword(email, onSuccess, onFailure, 'resetPasswordConfirmationModal', isPasskeyFlow ? { pk: true } : {}));
    };

    return (
        <>
            <Text
                is='h1'
                fontSize={fontSizes.lg}
                fontWeight={fontWeights.bold}
                lineHeight={lineHeights.tight}
                marginBottom={4}
            >
                {getText('emailSent')}
            </Text>

            <Box marginBottom={4}>
                <Text
                    is='p'
                    fontSize={fontSizes.md}
                    lineHeight={lineHeights.base}
                >
                    {getText('confirmationMessage')} <strong>{email}</strong>
                    {'. '}
                    {isPasskeyFlow ? getText('confirmationMessagePasskey') : getText('confirmationMessage2')}
                </Text>
                {!resendSuccess && (
                    <Text
                        display='block'
                        marginTop={3}
                        fontSize={fontSizes.sm}
                        color={colors.gray}
                    >
                        {getText('didntGetEmail')}{' '}
                        <Link
                            color={colors.blue}
                            display='inline'
                            underline={true}
                            onClick={handleResend}
                        >
                            {getText('resend')}
                        </Link>
                        .
                    </Text>
                )}
                {resendError ? (
                    <Box
                        role='alert'
                        css={styles.errorAlert}
                        marginTop={3}
                    >
                        {resendError}
                    </Box>
                ) : null}
            </Box>

            <Button
                variant='primary'
                width='100%'
                paddingY={space[3]}
                onClick={handleOk}
            >
                {getText('confirmButton')}
            </Button>
        </>
    );
};

const styles = {
    errorAlert: {
        color: colors.red
    }
};

export default wrapFunctionalComponent(EmailSentConfirmation, 'EmailSentConfirmation');
